import "es6-shim";
import * as React from 'react';
import RegionCodeValidator from '../../../services/Validator/RegionCode/RegionCodeValidator';
import '../../../styles/Form.css';
import { AddressAutoComplete } from '../../AddressAutoComplete/AddressAutoComplete';
import { CountryCodes } from "../../../constants/countries";
import { ILabelSelect, IGenericsLang } from "../../../services/Language/Types";
import { SelectControl } from "../../FormControls/SelectControl";
import { AddressCheckin } from "../../../dtos/KioskDtos.dtos";
import { InputControl } from "../../FormControls/InputControl";
import { FormContainerInline } from "../../FormControls/FormContainerInline";
import { formatInputAccordingToMask } from "../../../services/Validator/PhoneNumber/PhoneNumberValidator";
import { CheckBoxGroup } from "../../customFormComponents/CheckBoxGroup/CheckBoxGroup";

export interface IAddressEditProps {
    lang: string;
    countryMappedStrings: any;
    countryList: ILabelSelect[];
    stateList: any;
    address: AddressCheckin;
    contactPointOptions: ILabelSelect[];
    index: number;
    text: IGenericsLang;
    focusedInputName: string;
    onDeleteAddress: () => void;
    onAddressEdit: (address: AddressCheckin) => void;
    onFocus: (pipe: (value: any) => void, value: any, mask: string, id: string) => void;
    disconnectKeyboard: () => void;
}

export const AddressEdit = (props: IAddressEditProps) => {
    const address = props.address;
    const onAddressChange = (change: (addressState: AddressCheckin) => void) => {
        const newAddressState = Object.assign({}, address);
        change(newAddressState);
        props.onAddressEdit(newAddressState)
    }

    const textString = props.text;
    if (!address) {
        return null;
    }
    const stateOptions: ILabelSelect[] = address.country && props.stateList[address.country] ? props.stateList[address.country] : [];
    const regionCodeMask = RegionCodeValidator.getRegionTextMaskByCountry(address.country);

    const contactPointTypeSelectRender = () => (
        <SelectControl
            onChange={(contactPointType: string) => onAddressChange(addressState => addressState.contactPointType = contactPointType)}
            placeholder={textString.labels.TypeLabel}
            value={address.contactPointType}
            options={props.contactPointOptions}
            onFocus={props.disconnectKeyboard}
        />
    );
    const countrySelectRender = () => (
        <SelectControl
            onFocus={() => props.disconnectKeyboard()}
            placeholder={textString.labels.CountryLabel}
            onChange={(country: string) => onAddressChange(s => {
                s.country = country;
                s.city = "";
                s.zipCode = "";
                s.state = "";
                s.streetAddress = "";
            })}
            value={address.country}
            options={props.countryList}
        />
    );

    const regionSelectRender = () => (
        stateOptions.any()
            ? (
                <SelectControl
                    onFocus={() => props.disconnectKeyboard()}
                    placeholder={props.countryMappedStrings.regionLabel[address.country]}
                    onChange={(value: string) => onAddressChange(s => {
                        s.state = value;
                        s.zipCode = "";
                        s.city = "";
                        s.streetAddress = "";
                    })}
                    value={address.state}
                    options={stateOptions}
                    isInvalid={!address.state}
                />
            )
            : null
    );

    const streetAddressInputRender = () => (
        address.country === CountryCodes.CA || address.country === CountryCodes.US
            ? (<AddressAutoComplete
                id="AddressAutocomplete"
                closeText={textString.labels.CloseButton}
                onClose={() => props.disconnectKeyboard()}
                langCode={props.lang}
                isValid={address.streetAddress !== ""}
                validationMessage={textString.validations.StreetAddressValidation}
                placeholder={textString.labels.StreetAddressLabel}
                countryCode={address.country}
                value={address.streetAddress}
                onAddressChange={(val) => onAddressChange(newAddress => {
                    newAddress.state = val.state;
                    newAddress.streetAddress = val.streetAddress;
                    newAddress.city = val.city;
                    newAddress.zipCode = val.zipCode;
                })}
                onChange={(val) => onAddressChange(s => s.streetAddress = val)}
                onFocus={() => props.onFocus((value: string) => onAddressChange(s => s.streetAddress = value), address.streetAddress, "", "streetAddress" + props.index)}
                focused={props.focusedInputName === ("streetAddress" + props.index)}
            />
            )
            : (
                <InputControl
                    placeHolder={textString.labels.StreetAddressLabel}
                    onChange={(value: string) => onAddressChange(s => s.streetAddress = value)}
                    value={address.streetAddress}
                    isValid={address.streetAddress.trim() !== ""}
                    readOnly={false}
                    onFocus={() => { props.onFocus((value: string) => onAddressChange(s => s.streetAddress = value), address.streetAddress, "", "streetAddress" + props.index) }}
                    focused={props.focusedInputName === ("streetAddress" + props.index)}
                />
            )
    );

    const cityInputRender = () => (
        <InputControl
            placeHolder={textString.labels.CityLabel}
            onChange={(value: string) => onAddressChange(s => s.city = value)}
            value={address.city}
            isValid={address.city.trim() !== ""}
            onFocus={() => props.onFocus((value: string) => onAddressChange(s => s.city = value), address.city, "", "city")}
            focused={props.focusedInputName === "city"}
        />
    );

    const regionCodeRender = () => (
        <InputControl
            value={formatInputAccordingToMask(address.zipCode, RegionCodeValidator.getRegionTextMaskByCountry(address.country))}
            onChange={(value) => onAddressChange(s => s.zipCode = value)}
            isValid={RegionCodeValidator.validateRegionCodeByCountry(address.country, address.zipCode)}
            placeHolder={props.countryMappedStrings.regionCodeLabel[address.country] || props.countryMappedStrings.regionCodeLabel.US}
            onFocus={() => { props.onFocus((value: string) => onAddressChange(s => s.zipCode = value), address.zipCode, regionCodeMask, "zipCode") }}
            focused={props.focusedInputName === "zipCode"}
        />
    );

    const isPreferredRender = () => (
        <div style={{ fontSize: "30px" }}>
            <CheckBoxGroup
                componentId="preferredAddressContainer"
                options={[{ label: textString.labels.PreferredAddressLabel, checked: address.isPreferred, disabled: false, optionId: "preferredAddress" }]}
                onChange={(checked: boolean) => onAddressChange(s => s.isPreferred = checked)}
            />
        </div>
    )
    return (
        <div className="col-sm-12">
            <FormContainerInline
                onDelete={props.onDeleteAddress}
                elements={[
                    contactPointTypeSelectRender(),
                    countrySelectRender(),
                    regionSelectRender(),
                    streetAddressInputRender(),
                    cityInputRender(),
                    regionCodeRender(),
                    isPreferredRender()
                ]}
            />
        </div>
    );
}




// WEBPACK FOOTER //
// ./src/components/PatientOverview/EditComponents/AddressEdit.tsx